import { create } from 'zustand';
import {
  searchTemplates,
  getTemplateCategories,
  getTemplateDetail,
  type N8nTemplateSummary,
  type N8nTemplateDetail,
  type N8nTemplateCategory,
} from '../lib/n8n-template-api';
import { API_BASE_URL } from '../lib/api-config';

const CATEGORIES_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

// AI recommendation type matching the API response
export interface TemplateRecommendation {
  templateId: number;
  name: string;
  description: string;
  totalViews: number;
  user: { name: string; verified: boolean };
  nodes: Array<{ name: string; displayName: string; iconData?: { type: string; fileBuffer: string } }>;
  categories: Array<{ id: number; name: string }>;
  aiExplanation: string;
  suggestedModifications: string;
  relevanceScore: number;
}

interface TemplateState {
  // Search / filter
  searchQuery: string;
  selectedCategory: string | null;
  currentPage: number;
  rowsPerPage: number;

  // Data
  templates: N8nTemplateSummary[];
  totalCount: number;
  categories: N8nTemplateCategory[];
  categoriesLastFetchedAt: number | null;

  // Detail modal
  selectedTemplate: N8nTemplateDetail | null;

  // Loading / error
  isLoading: boolean;
  isDetailLoading: boolean;
  error: string | null;

  // AI search
  aiSearchQuery: string;
  aiSearchActive: boolean;
  aiSearchLoading: boolean;
  aiSearchStatus: string;
  aiRecommendations: TemplateRecommendation[];
  aiSearchError: string | null;
  aiSearchAbortController: AbortController | null;

  // Actions
  setSearchQuery: (query: string) => void;
  setSelectedCategory: (categoryId: string | null) => void;
  setCurrentPage: (page: number) => void;
  fetchTemplates: () => Promise<void>;
  fetchCategories: () => Promise<void>;
  openTemplateDetail: (id: number) => Promise<void>;
  closeTemplateDetail: () => void;
  resetFilters: () => void;
  startAISearch: (query: string) => Promise<void>;
  clearAISearch: () => void;
  cancelAISearch: () => void;
}

export const useTemplateStore = create<TemplateState>((set, get) => ({
  searchQuery: '',
  selectedCategory: null,
  currentPage: 1,
  rowsPerPage: 20,

  templates: [],
  totalCount: 0,
  categories: [],
  categoriesLastFetchedAt: null,

  selectedTemplate: null,

  isLoading: false,
  isDetailLoading: false,
  error: null,

  // AI search defaults
  aiSearchQuery: '',
  aiSearchActive: false,
  aiSearchLoading: false,
  aiSearchStatus: '',
  aiRecommendations: [],
  aiSearchError: null,
  aiSearchAbortController: null,

  setSearchQuery: (searchQuery) => set({ searchQuery, currentPage: 1 }),

  setSelectedCategory: (selectedCategory) => set({ selectedCategory, currentPage: 1 }),

  setCurrentPage: (currentPage) => set({ currentPage }),

  fetchTemplates: async () => {
    const { searchQuery, selectedCategory, currentPage, rowsPerPage } = get();
    set({ isLoading: true, error: null });
    try {
      const result = await searchTemplates({
        search: searchQuery || undefined,
        category: selectedCategory || undefined,
        page: currentPage,
        rows: rowsPerPage,
      });
      set({
        templates: result.workflows,
        totalCount: result.totalWorkflows,
      });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to fetch templates' });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchCategories: async () => {
    const { categoriesLastFetchedAt, categories } = get();
    if (
      categories.length > 0 &&
      categoriesLastFetchedAt &&
      Date.now() - categoriesLastFetchedAt < CATEGORIES_CACHE_TTL
    ) {
      return;
    }
    try {
      const cats = await getTemplateCategories();
      set({ categories: cats, categoriesLastFetchedAt: Date.now() });
    } catch (err) {
      console.error('Failed to fetch template categories:', err);
    }
  },

  openTemplateDetail: async (id) => {
    set({ isDetailLoading: true });
    try {
      const detail = await getTemplateDetail(id);
      set({ selectedTemplate: detail });
    } catch (err) {
      console.error('Failed to fetch template detail:', err);
    } finally {
      set({ isDetailLoading: false });
    }
  },

  closeTemplateDetail: () => set({ selectedTemplate: null }),

  resetFilters: () => set({ searchQuery: '', selectedCategory: null, currentPage: 1 }),

  startAISearch: async (query: string) => {
    // Cancel any existing search
    const { aiSearchAbortController: existingController } = get();
    if (existingController) existingController.abort();

    const controller = new AbortController();
    set({
      aiSearchQuery: query,
      aiSearchActive: true,
      aiSearchLoading: true,
      aiSearchStatus: 'Starting AI search...',
      aiRecommendations: [],
      aiSearchError: null,
      aiSearchAbortController: controller,
    });

    try {
      const res = await fetch(`${API_BASE_URL}/api/templates/ai-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ query }),
        signal: controller.signal,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Search failed' })) as { error?: string };
        set({
          aiSearchLoading: false,
          aiSearchError: err.error || `Search failed (${res.status})`,
        });
        return;
      }

      const reader = res.body?.getReader();
      if (!reader) {
        set({ aiSearchLoading: false, aiSearchError: 'No response stream' });
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      const processSSELine = (line: string) => {
        if (!line.startsWith('data: ')) return;
        const jsonStr = line.slice(6).trim();
        if (!jsonStr) return;

        try {
          const event = JSON.parse(jsonStr) as {
            type: string;
            message?: string;
            data?: TemplateRecommendation[];
          };

          switch (event.type) {
            case 'status':
              set({ aiSearchStatus: event.message || '' });
              break;
            case 'recommendations':
              set({
                aiRecommendations: event.data || [],
                aiSearchLoading: false,
                aiSearchStatus: '',
              });
              break;
            case 'error':
              set({
                aiSearchLoading: false,
                aiSearchError: event.message || 'Search failed',
                aiSearchStatus: '',
              });
              break;
          }
        } catch {
          // Skip malformed SSE lines
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          processSSELine(line);
        }
      }

      // Process any remaining data in the buffer (e.g. final event without trailing newline)
      if (buffer.trim()) {
        processSSELine(buffer.trim());
      }

      // Guarantee loading state is cleared when stream ends
      if (get().aiSearchLoading) {
        set({ aiSearchLoading: false, aiSearchStatus: '' });
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      set({
        aiSearchLoading: false,
        aiSearchError: err instanceof Error ? err.message : 'Search failed',
        aiSearchStatus: '',
      });
    }
  },

  clearAISearch: () => {
    const { aiSearchAbortController } = get();
    if (aiSearchAbortController) aiSearchAbortController.abort();
    set({
      aiSearchQuery: '',
      aiSearchActive: false,
      aiSearchLoading: false,
      aiSearchStatus: '',
      aiRecommendations: [],
      aiSearchError: null,
      aiSearchAbortController: null,
    });
  },

  cancelAISearch: () => {
    const { aiSearchAbortController } = get();
    if (aiSearchAbortController) aiSearchAbortController.abort();
    set({
      aiSearchLoading: false,
      aiSearchStatus: '',
      aiSearchAbortController: null,
    });
  },
}));
